import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';

import {

  Box, Button, MenuItem, Paper, Select, Stack, Typography,

} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';

import AppToast from '../../../shared/components/AppToast';

import DataState from '../../../shared/components/DataState';
import { resolveUserFacingError } from '../../../shared/utils/actionFeedback';
import { SCOPE2_MAPPING_REFRESH_FAILED } from '../../../shared/constants';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import {
  managementReportingYearOptions,
} from '../../../shared/utils/reportingYear';

import useOfficeMaster from '../hooks/useOfficeMaster';

import OfficeToolbar from './OfficeToolbar';

import OfficeTable from './OfficeTable';



import OfficeFormDialog from './OfficeFormDialog';
import ElectricitySupplierManagerDialog from './ElectricitySupplierManagerDialog';

export default function OfficePage() {

  const { t } = useTranslation();
  const navigate = useNavigate();

  const {

    offices, suppliers, fiscalYear, setFiscalYear, loading, error, reload, reloadSuppliers,

    filters, setFilter, page, setPage, rowsPerPage, setRowsPerPage,

    saveOffice,

  } = useOfficeMaster();
  usePagePresentation({ loading });
  const fiscalYears = managementReportingYearOptions({ now: new Date(), yearsAhead: 1 });



  // `open` is kept apart from the selected row so the dialog can play its exit

  // transition instead of blanking out the moment it closes.

  const [formOpen, setFormOpen] = useState(false);
  const [supplierManagerOpen, setSupplierManagerOpen] = useState(false);

  const [editing, setEditing] = useState(null);

  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null); // { severity, message }

  const openCreate = () => { setEditing(null); setFormOpen(true); };

  const openEdit = (office) => { setEditing(office); setFormOpen(true); };



  // The dialog hands over the finished request body (see `model/officeForm.js`).

  // A failure keeps the dialog open with the values still in it — closing it

  // would throw away what the user typed for no reason.

  const handleSubmit = async (payload) => {

    const action = editing ? 'update' : 'create';
    setSaving(true);

    try {

      const saved = await saveOffice(editing, payload);
      const assignmentImpact = saved.assignmentMutation;
      const mappingRefreshFailed = assignmentImpact?.mapping_refresh?.outcome === 'FAILED';
      const mappingRefreshMessage = assignmentImpact?.mapping_refresh?.message;
      const mappingRefreshKey = /^[A-Z][A-Z0-9_]*$/.test(mappingRefreshMessage || '')
        ? mappingRefreshMessage
        : SCOPE2_MAPPING_REFRESH_FAILED;
      const affectedRecords = Number(assignmentImpact?.affected_raw_records || 0);
      const affectedYears = Array.isArray(assignmentImpact?.affected_fiscal_years)
        ? assignmentImpact.affected_fiscal_years
        : [];

      if (mappingRefreshFailed) {
        setToast({
          severity: 'warning',
          message: t(mappingRefreshKey),
        });
      } else if (affectedYears.length > 0) {
        setToast({
          severity: 'warning',
          message: t('office.toast.assignmentNeedsRecalculation', {
            years: affectedYears.join(', '),
          }),
          actionLabel: t('office.toast.goToDataInput'),
          onAction: () => navigate('/data-input'),
        });
      } else {
        setToast({
          severity: 'success',
          message: affectedRecords > 0
            ? t('office.toast.assignmentSavedWithImpact', { count: affectedRecords })
            : t(`common.feedback.${action}.success`),
        });
      }

      setFormOpen(false);

    } catch (err) {

      if (err?.wasOfficeSaved) {
        setFormOpen(false);
        setEditing(null);
      }

      if (!err?.wasOfficeSaved && err?.fieldErrors?.length) {
        setToast(null);
        throw err;
      }

      setToast({
        severity: 'error',
        message: resolveUserFacingError(
          err,
          err?.wasOfficeSaved
            ? t('office.toast.officeSavedAssignmentFailed')
            : t(`common.feedback.${action}.failure`),
        ),
      });

    } finally {

      setSaving(false);

    }

  };



  return (

    <Box component="section" aria-labelledby="office-title" className="page-stack">

      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>

        <Stack

          direction={{ xs: 'column', sm: 'row' }}

          justifyContent="space-between"

          alignItems={{ xs: 'stretch', sm: 'center' }}

          spacing={2}

        >

          <Stack spacing={0.5} sx={{ minWidth: 0 }}>

            <Typography variant="h6" fontWeight={700}>{t('office.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              {t('office.subtitle')}
            </Typography>

          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Select
              size="small"
              value={fiscalYear}
              onChange={event => setFiscalYear(Number(event.target.value))}
              inputProps={{ 'aria-label': t('office.assignment.fiscalYear') }}
              sx={{ minWidth: 138, alignSelf: { xs: 'stretch', sm: 'center' } }}
            >
              {fiscalYears.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
            </Select>
            <Button
              variant="outlined"
              startIcon={<TuneRoundedIcon />}
              onClick={() => setSupplierManagerOpen(true)}
              disabled={loading}
              sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
            >
              {t('office.supplierCatalog.manage')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={loading || Boolean(error)}
              sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, flexShrink: 0 }}
            >
              {t('office.addOffice')}
            </Button>
          </Stack>

        </Stack>

      </Paper>



      <DataState loading={loading} error={error} onRetry={reload} />



      {!error && (

        <>

          <OfficeToolbar value={filters.search} onChange={(v) => setFilter('search', v)} />



          <Paper variant="outlined">

            <OfficeTable

              offices={offices}

              filters={filters}

              onFilterChange={setFilter}

              page={page}

              rowsPerPage={rowsPerPage}

              onPageChange={setPage}

              onRowsPerPageChange={setRowsPerPage}

              onEdit={openEdit}

            />

          </Paper>

        </>

      )}



      <OfficeFormDialog

        open={formOpen}

        office={editing}
        suppliers={suppliers}
        fiscalYear={fiscalYear}

        saving={saving}

        onClose={() => setFormOpen(false)}

        onSubmit={handleSubmit}

      />

      <ElectricitySupplierManagerDialog
        open={supplierManagerOpen}
        onClose={() => setSupplierManagerOpen(false)}
        onCatalogChanged={() => reloadSuppliers().catch(() => {})}
      />



      <AppToast toast={toast} onClose={() => setToast(null)} />

    </Box>

  );

}
